import { MedChronAssetRouteSegments, MedChronAssetTabs } from '@/medical-chronology/asset/constants'
import { MedSummaryAssetRouteSegments, MedSummaryAssetTabs } from '@/medical-billing-summaries/asset/constants'
import { TimelineAssetRouteSegments, TimelineAssetTabs } from '@/timeline/asset/constants'

export const kRouteSegments = {
  chat: 'chat',
  assistant: 'assistant',
  chronology: 'medical-chronology',
  timeline: 'timeline',
  billing: 'billing-summaries',
  files: 'files',
  userFiles: 'user-files',
  referenceViewer: 'reference-viewer',
} as const

/** The default personal drive name */
export const kMyDriveName = 'my-drive'

/**
 * Centralized path builders and route patterns for matter-first routing.
 *
 * All path builders require a valid matterId. Callers should ensure they have
 * a matter context before building paths.
 *
 * Usage:
 * - Use `matterPaths.*` for building navigation URLs
 * - Use `routePatterns.*` for route matching with useMatch()
 *
 * @example
 * matterPaths.overview('123')                        // '/dashboard/m/123'
 * matterPaths.chat.feature('123', 'research')        // '/dashboard/m/123/${kRouteSegments.chat}/research'
 * matterPaths.chronology.index('123')                // '/dashboard/m/123/${kRouteSegments.chronology}'
 */
export const matterPaths = {
  /** Matter overview path (root of matter context) */
  overview: (matterId: string) => `/dashboard/m/${matterId}`,
  /** Matter settings path */
  settings: (matterId: string) => `/dashboard/m/${matterId}/settings`,

  /** Chat feature paths */
  chat: {
    /** Chat feature index (e.g., research, assistant, caselaw) */
    feature: (matterId: string, feature: string) => `/dashboard/m/${matterId}/${kRouteSegments.chat}/${feature}`,
    /** Specific conversation within a chat feature */
    conversation: (matterId: string, feature: string, chatId: string) => `/dashboard/m/${matterId}/${kRouteSegments.chat}/${feature}/${chatId}`,
  },

  /** Medical Chronology paths (the matter's living chronology) */
  chronology: {
    /** The living chronology (defaults to the All Events tab) */
    index: (matterId: string) => `/dashboard/m/${matterId}/${kRouteSegments.chronology}`,
    /** Specific tab within the living chronology */
    tab: (matterId: string, tab: MedChronAssetTabs) => `/dashboard/m/${matterId}/${kRouteSegments.chronology}/${tab}`,
    /** Add Files page (stage uploads/matter files, then generate or update) */
    addFiles: (matterId: string) => `/dashboard/m/${matterId}/${kRouteSegments.chronology}/${MedChronAssetRouteSegments.ADD_FILES}`,
    /** Download-only page for deprecated chronology reports */
    legacyReports: (matterId: string) => `/dashboard/m/${matterId}/${kRouteSegments.chronology}/${MedChronAssetRouteSegments.LEGACY_REPORTS}`,
  },

  /** Timeline (Single Matter) paths — the matter's living timeline asset */
  timeline: {
    /** The living timeline (defaults to the All Events tab) */
    index: (matterId: string) => `/dashboard/m/${matterId}/${kRouteSegments.timeline}`,
    /** Specific tab within the living timeline */
    tab: (matterId: string, tab: TimelineAssetTabs) => `/dashboard/m/${matterId}/${kRouteSegments.timeline}/${tab}`,
    /** Add Files page (stage uploads/matter files, then generate or update) */
    addFiles: (matterId: string) => `/dashboard/m/${matterId}/${kRouteSegments.timeline}/${TimelineAssetRouteSegments.ADD_FILES}`,
  },

  /** Medical Billing Summary paths */
  billing: {
    /** The billing-summaries feature root: the matter's living billing summary */
    index: (matterId: string) => `/dashboard/m/${matterId}/${kRouteSegments.billing}`,
    /** Specific tab within the living billing summary */
    assetTab: (matterId: string, tab: MedSummaryAssetTabs) => `/dashboard/m/${matterId}/${kRouteSegments.billing}/${tab}`,
    /** Add Files page (stage uploads/matter files, then generate or update the living summary) */
    assetAddFiles: (matterId: string) => `/dashboard/m/${matterId}/${kRouteSegments.billing}/${MedSummaryAssetRouteSegments.ADD_FILES}`,
  },

  /** Files/Drive paths */
  files: {
    /** Drive root (e.g., my-drive) */
    drive: (matterId: string, driveName: string) => `/dashboard/m/${matterId}/${kRouteSegments.files}/${driveName}`,
    /** Specific folder within a drive */
    folder: (matterId: string, driveName: string, folderPath: string) => `/dashboard/m/${matterId}/${kRouteSegments.files}/${driveName}/${folderPath}`,
  },

  /** User Files paths (new pipeline) */
  userFiles: {
    /** User files root for a matter */
    root: (matterId: string) => `/dashboard/m/${matterId}/${kRouteSegments.userFiles}`,
    /** Specific folder within user files */
    folder: (matterId: string, folderId: string) => `/dashboard/m/${matterId}/${kRouteSegments.userFiles}/${folderId}`,
  },

  /** Reference Viewer paths */
  referenceViewer: {
    caselaw: (matterId: string | null, idType: string, id: string) =>
      matterId
        ? `/dashboard/m/${matterId}/${kRouteSegments.referenceViewer}/caselaw/${idType}/${id}`
        : `/dashboard/${kRouteSegments.referenceViewer}/caselaw/${idType}/${id}`,
    lrr: (matterId: string | null, s3Path: string) =>
      matterId ? `/dashboard/m/${matterId}/${kRouteSegments.referenceViewer}/lrr/${s3Path}` : `/dashboard/${kRouteSegments.referenceViewer}/lrr/${s3Path}`,
    userFile: (matterId: string, referenceId: string) => `/dashboard/m/${matterId}/${kRouteSegments.referenceViewer}/user-file/${referenceId}`,
    writingSample: (matterId: string, referenceId: string) => `/dashboard/m/${matterId}/${kRouteSegments.referenceViewer}/writing-sample/${referenceId}`,
    legacyFile: (matterId: string, relativePath: string) =>
      `/dashboard/m/${matterId}/${kRouteSegments.referenceViewer}/legacy-file?viewFile=${encodeURIComponent(relativePath)}`,
  },
} as const

// =============================================================================
// Route Patterns (for useMatch)
// =============================================================================

/**
 * Route patterns for matching URLs with useMatch().
 *
 * @example
 * const isChronologyRoute = useMatch(routePatterns.chronology.matterFirst)
 * const isAssistantRoute = useMatch(routePatterns.chat.matterFirstFeature)
 */
export const routePatterns = {
  /** Matter overview route — matches /dashboard/m/:matterId with no sub-path */
  matterOverview: `/dashboard/m/:matterId`,

  /** Medical Chronology route patterns */
  chronology: {
    /** Matter-first URL pattern for useMatch */
    matterFirst: `/dashboard/m/:matterId/${kRouteSegments.chronology}/*`,
  },

  /** Timeline (Single Matter) route patterns */
  timeline: {
    /** Matter-first URL pattern for useMatch */
    matterFirst: `/dashboard/m/:matterId/${kRouteSegments.timeline}/*`,
  },

  /** Medical Billing Summaries route patterns */
  billingSummaries: {
    /** Matter-first URL pattern for useMatch */
    matterFirst: `/dashboard/m/:matterId/${kRouteSegments.billing}/*`,
  },

  /** Chat feature route patterns */
  chat: {
    /** Matter-first feature-specific pattern for useMatch */
    matterFirstFeature: `/dashboard/m/:matterId/${kRouteSegments.chat}/:chatFeature/*`,
  },

  /** Files/Drive route patterns */
  files: {
    /** Matter-first URL pattern for useMatch */
    matterFirst: `/dashboard/m/:matterId/${kRouteSegments.files}/:driveName/*`,
  },

  /** User Files route patterns (new pipeline) */
  userFiles: {
    /** Matter-first URL pattern for useMatch */
    matterFirst: `/dashboard/m/:matterId/${kRouteSegments.userFiles}/*`,
  },
} as const
